/**
 * Binance Service
 * 
 * This service handles:
 * 1. REST API calls for initial market data (snapshot)
 * 2. WebSocket connections for live price updates
 * 3. Market data management and updates
 */

export interface BinanceTickerData {
  symbol: string;
  priceChange: string;
  priceChangePercent: string;
  weightedAvgPrice: string;
  prevClosePrice: string;
  lastPrice: string;
  lastQty: string;
  bidPrice: string;
  bidQty: string;
  askPrice: string;
  askQty: string;
  openPrice: string;
  highPrice: string;
  lowPrice: string;
  volume: string;
  quoteVolume: string;
  openTime: number;
  closeTime: number;
  firstId: number;
  lastId: number;
  count: number;
}

export interface MarketData {
  symbol: string;
  name: string;
  price: string;
  change: string;
  isPositive: boolean;
  volume: string;
  high24h: string;
  low24h: string;
  openPrice: string;
  lastUpdated: number;
  category: string;
}

export interface WebSocketMessage {
  stream: string;
  data: any;
}

export class BinanceService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private marketData = new Map<string, MarketData>();
  private listeners: Set<(data: Map<string, MarketData>) => void> = new Set();
  private isAuthenticated = false;
  private lastUpdateTime = 0;
  private updateThrottleMs = 50; // Throttle updates to max once per 50ms

  constructor() {
    // Don't connect automatically - wait for authentication
  }

  /**
   * Start the service when user is authenticated
   */
  public startService() {
    if (this.isAuthenticated) {
      return;
    }
    
    this.isAuthenticated = true;
    this.connectWebSocket();
  }

  /**
   * Stop the service when user logs out
   */
  public stopService() {
    if (!this.isAuthenticated) {
      return;
    }
    
    this.isAuthenticated = false;
    this.disconnectWebSocket();
  }

  /**
   * Check if service is authenticated and running
   */
  public isServiceRunning(): boolean {
    return this.isAuthenticated;
  }

  private connectWebSocket() {
    if (!this.isAuthenticated || this.isConnecting || this.ws?.readyState === WebSocket.OPEN) return;
    
    this.isConnecting = true;
    
    // Use the new ticker stream for all symbols
    this.ws = new WebSocket('wss://stream.binance.com:9443/ws/!ticker@arr');
    
    this.ws.onopen = () => {
      this.isConnecting = false;
      this.reconnectAttempts = 0;
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleWebSocketMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.ws.onclose = () => {
      this.isConnecting = false;
      this.handleReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.isConnecting = false;
    };
  }

  private disconnectWebSocket() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  /**
   * Fetch initial market data from Binance REST API
   * This gets a snapshot of all trading pairs with 24hr stats
   */
  async fetchInitialMarketData(): Promise<Map<string, MarketData>> {
    if (!this.isAuthenticated) {
      throw new Error('BinanceService: User must be authenticated to fetch market data');
    }

    try {
      const response = await fetch('https://api.binance.com/api/v3/ticker/24hr');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: BinanceTickerData[] = await response.json();
      
      // Process raw data
      
      // Process and filter the data
      const processedData = this.processTickerData(data);
      
      // Update the internal map
      this.marketData = processedData;
      
      // Notify subscribers
      this.notifySubscribers();
      
      return processedData;
      
    } catch (error) {
      console.error('Error fetching initial market data:', error);
      throw error;
    }
  }

  /**
   * Process raw ticker data from Binance API
   */
  private processTickerData(tickerData: BinanceTickerData[]): Map<string, MarketData> {
    const processed = new Map<string, MarketData>();
    
    tickerData.forEach(ticker => {
      // Only process USDT pairs for now (can be expanded later)
      if (ticker.symbol.endsWith('USDT')) {
        const price = parseFloat(ticker.lastPrice) || 0;
        const openPrice = parseFloat(ticker.openPrice) || 0;
        const volume = parseFloat(ticker.volume) || 0;
        const high24h = parseFloat(ticker.highPrice) || 0;
        const low24h = parseFloat(ticker.lowPrice) || 0;
        
        // Calculate percentage change: (current price - opening price) / opening price * 100
        let priceChangePercent = 0;
        if (openPrice > 0) {
          priceChangePercent = ((price - openPrice) / openPrice) * 100;
        }
        
        // Validate that we have valid numeric values
        if (isNaN(price) || isNaN(priceChangePercent) || isNaN(volume) || isNaN(high24h) || isNaN(low24h) || isNaN(openPrice)) {
          console.warn(`Invalid initial data for ${ticker.symbol}:`, { price, priceChangePercent, volume, high24h, low24h, openPrice });
          return; // Skip this ticker if data is invalid
        }
        
        // Get asset name from symbol
        const assetName = this.getAssetName(ticker.symbol);
        
        const marketData: MarketData = {
          symbol: ticker.symbol,
          name: assetName,
          price: price.toFixed(this.getDecimalPlaces(price)),
          change: this.formatPercentageChange(priceChangePercent),
          isPositive: priceChangePercent >= 0,
          volume: this.formatVolume(volume),
          high24h: high24h.toFixed(this.getDecimalPlaces(high24h)),
          low24h: low24h.toFixed(this.getDecimalPlaces(low24h)),
          openPrice: openPrice.toFixed(this.getDecimalPlaces(openPrice)),
          lastUpdated: Date.now(),
          category: this.getAssetCategory(ticker.symbol)
        };
        
        processed.set(ticker.symbol, marketData);
      }
    });
    
    return processed;
  }

  /**
   * Get asset name from symbol
   */
  private getAssetName(symbol: string): string {
    const assetNames: { [key: string]: string } = {
      'BTCUSDT': 'Bitcoin',
      'ETHUSDT': 'Ethereum',
      'ADAUSDT': 'Cardano',
      'DOTUSDT': 'Polkadot',
      'LINKUSDT': 'Chainlink',
      'LTCUSDT': 'Litecoin',
      'XRPUSDT': 'Ripple',
      'SOLUSDT': 'Solana',
      'AVAXUSDT': 'Avalanche',
      'MATICUSDT': 'Polygon',
      'UNIUSDT': 'Uniswap',
      'DOGEUSDT': 'Dogecoin',
      'BCHUSDT': 'Bitcoin Cash',
      'XLMUSDT': 'Stellar',
      'XMRUSDT': 'Monero',
      'BNBUSDT': 'BNB',
      'ATOMUSDT': 'Cosmos',
      'NEARUSDT': 'NEAR Protocol',
      'FTMUSDT': 'Fantom',
      'ALGOUSDT': 'Algorand',
      'VETUSDT': 'VeChain',
      'ICPUSDT': 'Internet Computer',
      'FILUSDT': 'Filecoin',
      'TRXUSDT': 'TRON',
      'ETCUSDT': 'Ethereum Classic',
      'THETAUSDT': 'Theta Network',
      'CAKEUSDT': 'PancakeSwap',
      'NEOUSDT': 'NEO',
      'CHZUSDT': 'Chiliz',
      'HOTUSDT': 'Holo',
      'ZILUSDT': 'Zilliqa',
      'ZECUSDT': 'Zcash',
      'DASHUSDT': 'Dash',
      'WAVESUSDT': 'Waves',
      'QTUMUSDT': 'Qtum',
      'IOTAUSDT': 'IOTA',
      'OMGUSDT': 'OMG Network',
      'NANOUSDT': 'Nano',
      'BTTUSDT': 'BitTorrent',
      'WINUSDT': 'WINk',
      'STMXUSDT': 'StormX',
      'ANKRUSDT': 'Ankr',
      'CTXCUSDT': 'Cortex',
      'CHRUSDT': 'Chromia',
      'MANAUSDT': 'Decentraland',
      'SANDUSDT': 'The Sandbox',
      'AXSUSDT': 'Axie Infinity',
      'ENJUSDT': 'Enjin Coin',
      'GALAUSDT': 'Gala',
      'ROSEUSDT': 'Oasis Network',
      'ONEUSDT': 'Harmony',
      'IOTXUSDT': 'IoTeX',
      'HBARUSDT': 'Hedera',
      'VTHOUSDT': 'VeThor Token',
      'TFUELUSDT': 'Theta Fuel',
      'COTIUSDT': 'COTI',
      'CELOUSDT': 'Celo',
      'RENUSDT': 'Ren',
      'RSRUSDT': 'Reserve Rights',
      'OGNUSDT': 'Origin Protocol',
      'STORJUSDT': 'Storj',
      'SKLUSDT': 'Skale Network',
      'GRTUSDT': 'The Graph',
      'LRCUSDT': 'Loopring',
      'BANDUSDT': 'Band Protocol',
      'OCEANUSDT': 'Ocean Protocol',
      'ALPHAUSDT': 'Alpha Finance Lab',
      'AUDIOUSDT': 'Audius',
      'RLCUSDT': 'iExec RLC',
      'ARUSDT': 'Arweave',
      'FLMUSDT': 'Flamingo',
      'TRBUSDT': 'Tellor',
      'KEEPUSDT': 'Keep Network',
      'NUUSDT': 'NuCypher',
      'API3USDT': 'API3',
      'UMAUSDT': 'UMA',
      'BADGERUSDT': 'Badger DAO',
      'FARMUSDT': 'Harvest Finance',
      'KP3RUSDT': 'Keep3rV1',
      'HEGICUSDT': 'Hegic',
      'PICKLEUSDT': 'Pickle Finance',
      'CREAMUSDT': 'Cream Finance',
      'PERPUSDT': 'Perpetual Protocol',
      'RARIUSDT': 'Rarible',
      'SUPERUSDT': 'SuperFarm',
      'MASKUSDT': 'Mask Network',
      'DYDXUSDT': 'dYdX',
      'IMXUSDT': 'Immutable X',
      'GODSUSDT': 'Gods Unchained',
      'GALUSDT': 'Galxe',
      'OPUSDT': 'Optimism',
      'ARBUSDT': 'Arbitrum',
      'OSMOUSDT': 'Osmosis',
      'JUNOUSDT': 'Juno',
      'SCRTUSDT': 'Secret',
      'LUNAUSDT': 'Terra',
      'USTCUSDT': 'TerraUSD Classic',
      'KAVAUSDT': 'Kava',
      'HARDUSDT': 'Kava Lend',
      'SWPUSDT': 'Kava Swap',
      'BUSDUSDT': 'Binance USD',
      'TUSDUSDT': 'TrueUSD',
      'USDCUSDT': 'USD Coin',
      'DAIUSDT': 'Dai',
      'FRAXUSDT': 'Frax',
      'USDPUSDT': 'Pax Dollar',
      'GUSDUSDT': 'Gemini Dollar',
      'LUSDUSDT': 'Liquity USD',
      'SUSDUSDT': 'sUSD',
      'USDNUSDT': 'Neutrino USD',
      'USDKUSDT': 'USDK',
      'USDJUSDT': 'USDJ',
      'USDTUSDT': 'Tether',
    };
    
    return assetNames[symbol] || symbol.replace('USDT', '');
  }

  /**
   * Get appropriate decimal places for price formatting
   */
  private getDecimalPlaces(price: number): number {
    if (price >= 10000) return 2;       // Very high value assets (BTC, ETH, etc.)
    if (price >= 1000) return 2;        // High value assets (stocks, indices)
    if (price >= 100) return 2;         // Medium-high value assets
    if (price >= 10) return 3;          // Medium value assets
    if (price >= 1) return 4;           // Low-medium value assets
    if (price >= 0.1) return 4;         // Low value assets
    if (price >= 0.01) return 5;        // Very low value assets (capped at 5)
    if (price >= 0.001) return 5;       // Extremely low value assets (capped at 5)
    return 5;                            // Minimum value assets (capped at 5)
  }

  /**
   * Format volume for display
   */
  private formatVolume(volume: number): string {
    if (volume >= 1000000000) {
      return `${(volume / 1000000000).toFixed(2)}B`;
    } else if (volume >= 1000000) {
      return `${(volume / 1000000).toFixed(2)}M`;
    } else if (volume >= 1000) {
      return `${(volume / 1000).toFixed(2)}K`;
    } else {
      return volume.toFixed(2);
    }
  }

  /**
   * Safely format percentage change
   */
  private formatPercentageChange(change: number): string {
    if (isNaN(change) || !isFinite(change)) {
      return '+0.00%';
    }
    return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
  }

  private handleWebSocketMessage(data: any) {
    if (Array.isArray(data)) {
      // This is the ticker array from !ticker@arr stream
      
      // Process all tickers first, then notify subscribers once at the end
      let hasUpdates = false;
      data.forEach((ticker: any) => {
        if (this.updateMarketData(ticker)) {
          hasUpdates = true;
        }
      });
      
      // Only notify subscribers once after processing all tickers
      if (hasUpdates) {
        this.notifySubscribers();
      }
    }
  }

  private updateMarketData(ticker: any): boolean {
    const symbol = ticker.s;
    if (!symbol) return false;

    const price = parseFloat(ticker.c) || 0; // Close price (current price)
    const openPrice = parseFloat(ticker.o) || 0; // Open price (today's opening price)
    const volume = parseFloat(ticker.v) || 0; // Volume
    const high24h = parseFloat(ticker.h) || 0; // High 24h
    const low24h = parseFloat(ticker.l) || 0; // Low 24h
    
    // Calculate percentage change: (current price - opening price) / opening price * 100
    let priceChangePercent = 0;
    if (openPrice > 0) {
      priceChangePercent = ((price - openPrice) / openPrice) * 100;
    }

    // Validate data
    if (isNaN(price) || isNaN(priceChangePercent) || isNaN(volume) || isNaN(high24h) || isNaN(low24h) || isNaN(openPrice)) {
      console.warn(`Invalid data received for ${symbol}:`, { price, priceChangePercent, volume, high24h, low24h, openPrice });
      return false; // Skip this update if data is invalid
    }

    const assetName = this.getAssetName(symbol);
    const decimalPlaces = this.getDecimalPlaces(price);
    const formattedPrice = price.toFixed(decimalPlaces);
    const formattedVolume = this.formatVolume(volume);

    const marketData: MarketData = {
      symbol,
      name: assetName,
      price: formattedPrice,
      change: this.formatPercentageChange(priceChangePercent),
      volume: formattedVolume,
      high24h: high24h.toFixed(decimalPlaces),
      low24h: low24h.toFixed(decimalPlaces),
      isPositive: priceChangePercent >= 0,
      category: this.getAssetCategory(symbol),
      openPrice: openPrice.toFixed(decimalPlaces),
      lastUpdated: Date.now()
    };

    this.marketData.set(symbol, marketData);
    
    // Return true to indicate an update occurred
    return true;
  }

  /**
   * Handle WebSocket reconnection
   */
  private handleReconnect(): void {
    if (!this.isAuthenticated) {
      return;
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      
      setTimeout(() => {
        this.connectWebSocket();
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('Max reconnection attempts reached');
    }
  }

  /**
   * Subscribe to market data updates
   */
  subscribe(callback: (data: Map<string, MarketData>) => void): () => void {
    this.listeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all subscribers of data updates (with throttling)
   */
  private notifySubscribers(): void {
    const now = Date.now();
    
    // Throttle updates to prevent excessive notifications
    if (now - this.lastUpdateTime < this.updateThrottleMs) {
      return;
    }
    
    this.lastUpdateTime = now;
    
    this.listeners.forEach(callback => {
      try {
        callback(this.marketData);
      } catch (error) {
        console.error('Error in subscriber callback:', error);
      }
    });
  }

  /**
   * Get current market data
   */
  getMarketData(): Map<string, MarketData> {
    return this.marketData;
  }

  /**
   * Get market data for specific symbols
   */
  getMarketDataForSymbols(symbols: string[]): MarketData[] {
    return symbols
      .map(symbol => this.marketData.get(symbol))
      .filter(Boolean) as MarketData[];
  }

  /**
   * Get market data for a specific category
   */
  getMarketDataByCategory(category: string): MarketData[] {
    const allData = Array.from(this.marketData.values());
    
    switch (category.toLowerCase()) {
      case 'crypto':
        return allData.filter(item => item.symbol.endsWith('USDT'));
      case 'forex':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include forex pairs
        return [];
      case 'stocks':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include stocks
        return [];
      case 'commodities':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include commodities
        return [];
      default:
        return allData;
    }
  }

  private getAssetCategory(symbol: string): string {
    if (symbol.endsWith('USDT')) {
      return 'Crypto';
    } else if (symbol.endsWith('USD')) {
      return 'Forex';
    } else if (symbol.includes('GOLD') || symbol.includes('SILVER')) {
      return 'Commodities';
    } else {
      return 'Other';
    }
  }

  /**
   * Cleanup and disconnect
   */
  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnecting = false;
    this.listeners.clear();
  }
}

// Export singleton instance
export const binanceService = new BinanceService();
export default binanceService;
